import styled from 'styled-components';
import { Form, FlowConfig } from '@spotify-internal/content-platform-ui';
import { useT } from '@spotify-internal/i18n-core';
import { useEnrichardFormConfig } from '@hooks/useEnrichardFormConfig';
import { Loading } from '@components/shared/Loading/Loading';
import { CONTRIBUTOR_CREDITS_FLOW_ID } from '@helpers/enrichardForms';
import { formBodyHeight } from './constants';
import { ErrorState } from './ErrorState';

const LoadingContainer = styled.div`
  display: flex;
  min-height: ${formBodyHeight};
  justify-content: center;
  align-items: center;
`;

const FormWrapper = styled.div`
  textarea {
    min-height: 92px;
    min-width: 224px;
  }
`;

interface ContributorCreditsFormProps {
  onSuccess: () => void;
  // Carried through unswitched from page 1 (see Page1Data in helpers.ts) so
  // enrichard can pick whichever artist URI(s) it needs for the CPOP credits
  // lookup and for the ticket's artist_uri/other_artist_uri fields, based on
  // `problem`. The frontend doesn't decide which one(s) apply.
  destinationArtistUri: string;
  incorrectArtistUri: string;
  myArtistUri: string;
  currentArtistUri: string;
  releaseUris: string[];
  artistName: string;
  problem: string;
  comments: string;
  date: string;
  email: string;
}

export const ContributorCreditsForm = ({
  onSuccess,
  destinationArtistUri,
  incorrectArtistUri,
  myArtistUri,
  currentArtistUri,
  releaseUris,
  artistName,
  problem,
  comments,
  date,
  email,
}: ContributorCreditsFormProps) => {
  const t = useT();

  // The hook serializes this object to provide its own stable dependency, so
  // memoizing the same data here would duplicate that responsibility.
  const inputData = {
    destination_artist_uri: destinationArtistUri,
    incorrect_artist_uri: incorrectArtistUri,
    my_artist_uri: myArtistUri,
    current_artist_uri: currentArtistUri,
    release_uris: releaseUris,
    artist_name: artistName,
    problem,
    comments,
    date,
    email,
  };

  const { loading, configuration, error, errorStatus } = useEnrichardFormConfig(
    CONTRIBUTOR_CREDITS_FLOW_ID,
    inputData,
  );

  if (loading) {
    return (
      <LoadingContainer>
        <Loading size="md" smallHeight />
      </LoadingContainer>
    );
  }

  if (error || !configuration) {
    // 4xx means enrichard rejected something about *this* request (e.g. an
    // invalid artist URI) — its error message is written for end users, so
    // show it directly instead of the generic fallback used for 5xx/network
    // failures (which may not be user-appropriate, e.g. if it ever leaked
    // internal detail).
    const isClientError =
      errorStatus !== undefined && errorStatus >= 400 && errorStatus < 500;
    const genericText = t(
      'atlas.errors.error_loading_form_try_again',
      'There was a problem loading the form. Please try again or reach out to <a href="mailto:content-ops+ingestion@spotify.com">Content Ops</a>.',
      '"Content Ops" should not be translated and should be kept in English because it refers to a name of a Spotify team.',
    );

    return (
      <ErrorState
        title={t(
          'atlas.errors.error_loading_form',
          'Error loading form',
          'Error state after failed form loading',
        )}
        text={isClientError && error ? error : genericText}
        textHasLink={!isClientError || !error}
      />
    );
  }

  const handleSubmit = async (formData: Record<string, unknown>) => {
    try {
      const response = await fetch('/catalog/api/enrichard-form-submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flow_id: CONTRIBUTOR_CREDITS_FLOW_ID,
          field_values: formData,
        }),
      });

      if (!response.ok) {
        return { success: false };
      }

      const result = await response.json();

      if (result?.valid === false || result?.error) {
        return { success: false, field_errors: result.field_errors };
      }

      onSuccess();
      return { success: true };
    } catch {
      return { success: false };
    }
  };

  return (
    <FormWrapper>
      <Form
        // useEnrichardFormConfig returns content-platform-ui's IFlowConfig
        // (all fields optional, matching the JSON wire shape), while <Form>
        // expects the "hydrated" FlowConfig type it uses internally. The
        // fields <Form> actually reads off config are all present on
        // IFlowConfig, so this is a safe boundary cast rather than an `any`
        // escape hatch.
        config={configuration as unknown as FlowConfig}
        features={{
          recaptcha: { enabled: false },
          emailVerification: { enabled: false },
        }}
        onSubmit={handleSubmit}
      />
    </FormWrapper>
  );
};
